import type {
  CategoryDistributionBucket,
  SexDistributionBucket,
} from '@/types/progress';
import type { CategorySel } from '@/types';
import type { DonutSlice } from '@/components/ui/donut-chart';
import { categoricalSlot, NEUTRAL_SLOT } from '@/utils/chart-colors';
import { categoryLabel } from '@/utils/creator-helpers';
import { withPercentages, type RawSlice } from './distribution-percent';

// Every donut renders in a FIXED order, never sorted by size. Two reasons:
// a category keeps its hue and its position when the numbers move, and the
// ring's neighbouring pairs stay the ones the palette was validated
// against (see utils/chart-colors.ts).
const CATEGORY_ORDER: CategorySel[] = [
  { type: 'empresa', category: 1 },
  { type: 'empresa', category: 2 },
  { type: 'empresa', category: 3 },
  { type: 'persona', category: 1 },
  { type: 'persona', category: 2 },
  { type: 'persona', category: 3 },
  { type: 'persona', category: 4 },
];

function categoryKey(type: string, category: number): string {
  return `${type}:${category}`;
}

function otherSlice(count: number): RawSlice[] {
  if (count <= 0) return [];
  return [{ id: 'other', label: 'Otros', value: count, color: NEUTRAL_SLOT }];
}

// Anything the endpoint returns that is not one of the seven known tuples
// (a row from before the 132 remap, a type that was never set) folds into
// one neutral bucket rather than inventing an eighth hue for it.
function categoryCounts(buckets: CategoryDistributionBucket[]): {
  known: Map<string, number>;
  other: number;
} {
  const valid = new Set(
    CATEGORY_ORDER.map((c) => categoryKey(c.type, c.category))
  );
  const known = new Map<string, number>();
  let other = 0;
  for (const bucket of buckets) {
    const key = categoryKey(bucket.creator_type, bucket.category);
    if (valid.has(key)) known.set(key, (known.get(key) ?? 0) + bucket.count);
    else other += bucket.count;
  }
  return { known, other };
}

export function categorySlices(
  buckets: CategoryDistributionBucket[]
): DonutSlice[] {
  const { known, other } = categoryCounts(buckets);
  const raw: RawSlice[] = CATEGORY_ORDER.map((c, i) => ({
    id: categoryKey(c.type, c.category),
    label: categoryLabel(c.type, c.category),
    value: known.get(categoryKey(c.type, c.category)) ?? 0,
    color: categoricalSlot(i),
  }));
  return withPercentages([...raw, ...otherSlice(other)]);
}

const SEX_ORDER = [
  { key: 'H', label: 'Hombre' },
  { key: 'M', label: 'Mujer' },
];

// Empresa creators never get a sex — the bot only asks personas — and bulk
// imports were never asked either. So the empty bucket is a real answer,
// not missing data, and it keeps the denominator equal to the other two
// donuts. Any unexpected value lands there too.
export function sexSlices(buckets: SexDistributionBucket[]): DonutSlice[] {
  const counts = new Map<string, number>();
  for (const bucket of buckets) {
    const key = bucket.sex === 'H' || bucket.sex === 'M' ? bucket.sex : '';
    counts.set(key, (counts.get(key) ?? 0) + bucket.count);
  }
  const raw: RawSlice[] = SEX_ORDER.map((s, i) => ({
    id: s.key,
    label: s.label,
    value: counts.get(s.key) ?? 0,
    color: categoricalSlot(i),
  }));
  raw.push({
    id: 'none',
    label: 'Sin dato',
    value: counts.get('') ?? 0,
    color: NEUTRAL_SLOT,
  });
  return withPercentages(raw);
}
